import { Minus, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

type QuantityStepperProps = {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  disabled?: boolean
  className?: string
  decrementTestId?: string
  incrementTestId?: string
  valueTestId?: string
}

/**
 * Touch-first quantity control (44px targets on mobile). Value is clamped
 * to [min, max]; buttons disable at the bounds.
 */
export function QuantityStepper({
  value,
  onChange,
  min = 1,
  max,
  disabled = false,
  className,
  decrementTestId,
  incrementTestId,
  valueTestId,
}: QuantityStepperProps) {
  const decrement = () => onChange(Math.max(min, value - 1))
  const increment = () => onChange(max === undefined ? value + 1 : Math.min(max, value + 1))

  return (
    <div className={cn("inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted p-1", className)}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="rounded-full"
        data-testid={decrementTestId}
        onClick={decrement}
        disabled={disabled || value <= min}
        aria-label={t("ui.react.components.ui.quantity_stepper.decrease")}
      >
        <Minus />
      </Button>
      <span
        data-testid={valueTestId}
        className="min-w-6 text-center text-sm font-semibold tabular-nums"
      >
        {value}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="rounded-full"
        data-testid={incrementTestId}
        onClick={increment}
        disabled={disabled || (max !== undefined && value >= max)}
        aria-label={t("ui.react.components.ui.quantity_stepper.increase")}
      >
        <Plus />
      </Button>
    </div>
  )
}
